import React, { useEffect, useState } from 'react';
import { useRef, useCallback } from 'react';
import { Category } from 'src/model/Category';
import { Grade } from 'src/model/Grade';
import { Product } from 'src/model/Product';
import EditProductUI from './ui';
import { useDispatch } from 'react-redux';
import { setLoading } from 'src/redux/loading/loadingSlice';
import { toCurrency } from 'src/helpers/currency/toCurrency';
import { api } from 'src/services/api';
import { toast } from 'react-toastify';
import {
  EditProductBasic,
  FormInputGrade,
  FormInputGradeSize,
} from 'src/features/createProduct/types/types';
import { GradeSize } from 'src/model/GradeSize';
import { ProductStatus } from 'src/model/ProductStatus';

interface EditProductScreenProps {
  id: string;
  onClose: () => void;
}

export const EditProductScreen: React.FC<EditProductScreenProps> = ({
  id,
  onClose,
}): JSX.Element => {
  const [formData, setFormData] = useState<Product>({} as Product);
  const [categoryOptions, setCategoryOptions] = useState<Category[]>([]);
  const [inputTags, setInputTags] = useState<string[]>([]);
  const [filesImage, setFilesImage] = useState<File[]>([]);
  const [filesBase64Grade, setFilesBase64Grade] = useState<string>('');
  const [previewFiles, setPreviewFiles] = useState<string[]>([]);
  const [previewFilesGrade, setPreviewFilesGrade] = useState<string>('');
  const [previewGradesSizes, setPreviewGradesSizes] = useState<GradeSize[]>([]);
  const [isGrade, setIsGrade] = useState<boolean>(false);
  const [grade, setGrade] = useState<Grade[]>([]);
  const [gradeSize, setGradeSize] = useState<GradeSize[]>([]);
  const [shippingCost, setShippingCost] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [dataProduct, setData] = useState<Product>({} as Product);

  const dispatch = useDispatch();

  const formRef = useRef<Product>({} as Product);
  useEffect(() => {
    formRef.current = formData;
  }, [formData]);

  const saveBasicCurrent = useCallback(
    async (section?: string) => {
      if (section === 'basicInfo') {
        window.location.href = '/user-profile?tab=gerenciar-produtos';
        return;
      }
      const s = formRef.current;
      const payload: EditProductBasic = {
        id,
        name: s?.name ?? '',
        description: s?.description ?? '',
        sku: s?.sku ?? '',
        promotion: s?.promotion ?? '',
        freeShipping: toBool(s?.freeShipping),
        status: ProductStatus.INACTIVE,
        branding: s?.brand?.name ?? '',
        imagens: s?.imagens ?? [],
      };
      await api.put('/product/update-product-basic', payload);
      window.location.href = '/user-profile?tab=gerenciar-produtos';
    },
    [id]
  );

  const toBool = (v: unknown) =>
    v === true || v === 'true' || v === 1 || v === '1';

  const onGrade = (inputName: string) => (value: string) => {
    const vStr = value === 'true' ? 'true' : 'false';
    const vBool = vStr === 'true';

    setFormData((prev) => ({ ...prev, [inputName]: vStr }));

    if (inputName === 'isGrade') {
      setIsGrade(vBool);
    }

    if (inputName === 'grade') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) setGrade(parsed);
      } catch {
        /* silencia */
      }
    }
  };

  const handleShippingCalc = async (
    altura: string,
    largura: string,
    profundidade: string,
    peso: string
  ) => {
    try {
      const { data } = await api.post('/product/calc-frete', {
        altura,
        largura,
        profundidade,
        peso,
      });
      setShippingCost(toCurrency(data.frete));
    } catch {
      setShippingCost('Erro ao calcular o frete.');
    }
  };

  const getProductEdit = async () => {
    dispatch(setLoading(true));
    try {
      if (id) {
        const { data, status } = await api.get<Product>(`/product/one/${id}`);
        if (status === 200) {
          setSelectedCategories(data.category ?? []);
          setIsGrade(true);
          setFormData(data);
          setGrade(data.grade ?? []);
          setInputTags(data.tags ?? []);
          setPreviewFiles(data.imagens ?? []);
          setTags(data.tags ?? []);
          setData(data);

          await handleShippingCalc(
            data?.dimensions.height ?? '',
            data?.dimensions.width ?? '',
            data?.dimensions.depth ?? '',
            data?.dimensions.weight ?? ''
          );
        }
      }
    } catch {
      //toast.error('O produto deve passar por aprovação novamente');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const getCategory = async (): Promise<void> => {
    try {
      dispatch(setLoading(true));
      const response = await api.get<Category[]>('/category/find-all');

      if (response.data) {
        setCategoryOptions(response.data);
      }
      // eslint-disable-next-line
    } catch (error: any) {
      // resetFormRegister();
      let errorMessage =
        'Falha ao realizar o cadastro, tente novamente mais tarde';
      if (error.response && error.response.data) {
        errorMessage =
          typeof error.response.data === 'string'
            ? error.response.data
            : JSON.stringify(error.response.data);
      }
      toast.error(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSaveCategory = async (
    section: string,
    data: Partial<Product>
  ): Promise<void> => {
    const newCategories: string[] = [];
    let isCreateCategories = false;
    let isCreateTags = false;
    const newTags: string[] = [];

    if (data.category && data.category.length > 0) {
      data.category.forEach((category) => {
        if ((selectedCategories ?? []).includes(category)) {
          newCategories.push(category.id);
          isCreateCategories = true;
        }
      });
    }

    data.tags?.forEach((tag) => {
      if (!(tags ?? []).includes(tag)) {
        isCreateTags = true;
        newTags.push(tag);
      }
    });

    if (isCreateCategories) {
      try {
        const payload = {
          productId: id,
          categoryId: newCategories,
          status: ProductStatus.INACTIVE,
        };
        const response = await api.post('/product/update-categorie', payload);
        if (response.status === 200) {
          toast.success('Categoria salva com sucesso!');
          await saveBasicCurrent(section);
          getProductEdit();
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        let errorMessage =
          'Falha ao salvar a categoria, tente novamente mais tarde';
        if (error.response && error.response.data) {
          errorMessage =
            typeof error.response.data === 'string'
              ? error.response.data
              : JSON.stringify(error.response.data);
        }
        toast.error(errorMessage);
      } finally {
        dispatch(setLoading(false));
      }
    }

    if (isCreateTags) {
      try {
        const payload = {
          productID: id,
          tag: newTags,
          status: ProductStatus.INACTIVE,
        };
        const response = await api.post('/product/update-tags', payload);

        if (response.status === 200) {
          toast.success('Tags salvas com sucesso!');
          getProductEdit();
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        let errorMessage =
          'Falha ao salvar as tags, tente novamente mais tarde';
        if (error.response && error.response.data) {
          errorMessage =
            typeof error.response.data === 'string'
              ? error.response.data
              : JSON.stringify(error.response.data);
        }
        toast.error(errorMessage);
      } finally {
        dispatch(setLoading(false));
      }
    }
  };

  const handleSaveSection = async (
    section: string,
    data?: Partial<Product>
  ): Promise<void> => {
    try {
      dispatch(setLoading(true));

      if (section === 'basicInfo' && data) {
        const payload: EditProductBasic = {
          id,
          name: data?.name ?? formData.name,
          description: data?.description ?? formData.description,
          sku: data?.sku ?? formData.sku,
          promotion: data?.promotion ?? formData.promotion,
          freeShipping: toBool(data?.freeShipping ?? formData.freeShipping),
          status: ProductStatus.INACTIVE,
          branding: data?.brand?.name ?? formData.brand?.name ?? '',
          imagens: data?.imagens?.length ? data.imagens : formData.imagens,
        };

        await api.put('/product/update-product-basic', payload);
        await saveBasicCurrent(section);
        getProductEdit();

        toast.success('Informações Básicas salvas com sucesso!');
      }

      if (section === 'valuesVariations') {
        for (const item of grade) {
          const gradeExists = dataProduct.grade?.find(
            (itx) => itx.id === item.id
          );

          // Se a grade não existe, criar nova
          if (!gradeExists) {
            const payload = {
              productID: id,
              grades: [item],
              status: ProductStatus.INACTIVE,
            };
            await api.put('/product/create-grade', payload);
          } else {
            // Processar gradeSizes
            if (item.gradeSizes && item.gradeSizes.length > 0) {
              for (const gradeSize of item.gradeSizes) {
                const originalGrade = dataProduct.grade?.find(
                  (g) => g.id === item.id
                );
                const originalGradeSize = originalGrade?.gradeSizes?.find(
                  (gs) => gs.id === gradeSize.id
                );

                if (originalGradeSize && gradeSize.id) {
                  const wasModified =
                    String(originalGradeSize.size) !== String(gradeSize.size) ||
                    Number(originalGradeSize.stock) !==
                      Number(gradeSize.stock) ||
                    Number(originalGradeSize.price.price) !==
                      Number(gradeSize.price.price) ||
                    Number(originalGradeSize.price.discount) !==
                      Number(gradeSize.price.discount || 0);

                  if (wasModified) {
                    const priceId =
                      gradeSize.price.id || originalGradeSize.price?.id;
                    const payload = {
                      productID: id,
                      size: gradeSize.size,
                      stock: Number(gradeSize.stock),
                      status: ProductStatus.INACTIVE,
                      price: {
                        id: priceId,
                        price: Number(gradeSize.price.price),
                        discount: Number(gradeSize.price.discount || 0),
                      },
                    };

                    await api.put(
                      `/product/update-gradesize/${gradeSize.id}`,
                      payload
                    );
                  }
                }
                // GradeSize novo (sem ID) - precisaria de um endpoint de criação
                else if (!gradeSize.id) {
                  console.warn(
                    'GradeSize novo detectado, mas não há endpoint de criação configurado'
                  );
                  // Se houver endpoint de criação, implementar aqui
                }
              }
            }
          }
        }

        toast.success('Variações salvas com sucesso!');
        await saveBasicCurrent();
        await getProductEdit();
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar os dados');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSaveImages = async () => {
    if (filesImage.length === 0) {
      return;
    }

    try {
      dispatch(setLoading(true));

      const convertToBase64 = (
        file: File
      ): Promise<string | ArrayBuffer | null> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      };

      const base64Images = await Promise.all(
        filesImage.map((file: File) => convertToBase64(file))
      );

      const payload = {
        productID: id,
        images: base64Images,
        status: ProductStatus.INACTIVE,
      };

      const response = await api.put('/product/update-imagens', payload);

      toast.success('Imagens atualizadas com sucesso!');
      await saveBasicCurrent();
      getProductEdit();
    } catch (error) {
      toast.error('Erro ao atualizar imagens');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSelectedCaegories = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;

    if (!selectedId) return;

    const selectedCategory = categoryOptions.find(
      (cat) => cat.id === selectedId
    );

    if (selectedCategory) {
      setSelectedCategories([...selectedCategories, selectedCategory]);
    }
  };

  const handleSaveWarrantyDimensions = async () => {
    const freeShippingNew = toBool(formData.freeShipping);
    const freeShippingOld = toBool(dataProduct.freeShipping);

    const hasWarranty =
      Boolean(formData.warranty?.id) && Boolean(formData.warrantyValue);

    const hasDimensions =
      Boolean(formData.dimensions?.id) &&
      (Boolean(formData.dimensionsWidth) ||
        Boolean(formData.dimensionsHeight) ||
        Boolean(formData.dimensionsDepth) ||
        Boolean(formData.dimensionsWeight));

    const hasFreeShipping = freeShippingNew !== freeShippingOld;

    if (!hasWarranty && !hasDimensions && !hasFreeShipping) return;

    try {
      dispatch(setLoading(true));

      if (hasWarranty) {
        await api.put('/product/update-warranty', {
          id: formData.warranty!.id,
          warranty: formData.warrantyValue,
        });
      }

      if (hasDimensions) {
        await api.put('/product/update-dimensions', {
          id: formData.dimensions!.id,
          width: Number(formData.dimensionsWidth ?? 0),
          height: Number(formData.dimensionsHeight ?? 0),
          depth: Number(formData.dimensionsDepth ?? 0),
          weight: Number(formData.dimensionsWeight ?? 0),
        });
      }

      if (hasFreeShipping) {
        await api.put('/product/update-product-basic', {
          id,
          freeShipping: freeShippingNew,
        } as EditProductBasic);
      }

      toast.success('Garantia, dimensões e frete atualizados com sucesso!');
      await saveBasicCurrent();
      await getProductEdit();
    } catch {
      toast.error('Erro ao atualizar garantia/dimensões/frete');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleOnRemoveCategory = async (categoryId: string): Promise<void> => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(
        `/product/delete-category/${categoryId}/${id}`
      );
      if (response.status === 200) {
        toast.success('Categoria excluida com sucesso');
        setSelectedCategories((prev) =>
          prev.filter((cat) => cat.id !== categoryId)
        );
        //getCategory();
      }
    } catch (error) {
      toast.error('Algo deu Errado');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleRemoveTag = async (tag: string): Promise<void> => {
    try {
      dispatch(setLoading(true));
      const response = await api.delete(`/product/delete-tags/${tag}/${id}`);
      if (response.status === 200) {
        toast.success('Tag excluida com sucesso');
        getProductEdit();
      }
    } catch (error) {
      toast.error('Algo deu Errado');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleInputChange = (field: string) => (value: string) => {
    const keys = field.split('.');
    setFormData((prevData) => {
      const updatedData = { ...prevData };

      keys.reduce((acc, key, index) => {
        if (index === keys.length - 1) {
          acc[key] = value;
        } else {
          acc[key] = acc[key] || {};
        }
        return acc[key];
      }, updatedData);

      return updatedData;
    });
  };

  const handleRemoveGrade = async (id: string) => {
    console.log('entrou aqui');
    console.log(id);
    const tid = toast.loading('Removendo variação...');
    try {
      await api.delete(`/product/delete-grade-size/${id}`);

      setGrade((prev) =>
        prev.map((g) => {
          if (Array.isArray((g as any).sizes)) {
            const sizes = (g as any).sizes.filter(
              (s: GradeSize) => s.id !== id
            );
            return { ...g, sizes };
          }
          if (Array.isArray((g as any).gradeSizes)) {
            const gradeSizes = (g as any).gradeSizes.filter(
              (s: GradeSize) => s.id !== id
            );
            return { ...g, gradeSizes };
          }
          return g;
        })
      );

      toast.update(tid, {
        render: 'Variação removida',
        type: 'success',
        isLoading: false,
        autoClose: 2000,
      });
    } catch {
      toast.update(tid, {
        render: 'Falha ao remover variação',
        type: 'error',
        isLoading: false,
        autoClose: 2500,
      });
    }
  };

  const onChangeFormInput = (inputName: string) => (value: string | number) => {
    setFormData((prev) => {
      const updatedData = { ...prev, [inputName]: value };
      return updatedData;
    });
  };

  useEffect(() => {
    getProductEdit();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getCategory();
    // eslint-disable-next-line
  }, []);

  return (
    <EditProductUI
      formData={formData}
      formErrors={{}}
      categoryOptions={categoryOptions}
      selectedCategories={selectedCategories}
      inputTags={inputTags}
      filesImage={filesImage}
      previewFiles={previewFiles}
      isGrade={isGrade}
      grade={grade}
      setGrade={setGrade}
      filesImageGrade={[]}
      previewFilesGrade={previewFilesGrade}
      previewGradesSizes={previewGradesSizes}
      shippingCost={shippingCost}
      FileUpload={(e) => {
        const newFiles = Array.from(e.target.files || []);
        setFilesImage(newFiles);
        setPreviewFiles(newFiles.map((file) => URL.createObjectURL(file)));
      }}
      onDeleteFile={async ({
        index,
        productId,
        imageUrl,
      }: {
        index: number;
        productId?: string | number;
        imageUrl?: string;
      }) => {
        const fileName = imageUrl?.split('/').pop();

        try {
          await api.delete(`/product/delete-image/${productId}`, {
            data: [`/${fileName}`],
          });

          setFilesImage((prev) => {
            return prev.filter((_, i) => i !== index);
          });

          setPreviewFiles((prev) => {
            return prev.filter((_, i) => i !== index);
          });
        } catch (error) {
          console.log(error);
        }
      }}
      addQuantiValue={(e) => {
        e.preventDefault();
        const newGrade: Grade = {
          color: formData[FormInputGrade.color] || '',
          name: formData[FormInputGrade.nameGrade] || '',
          image: filesBase64Grade || '',
          gradeSizes: gradeSize?.length ? gradeSize : [],
        };

        const isDuplicate = grade.some((item) => {
          const isSameColor =
            item.color?.toLowerCase() === newGrade.color?.toLowerCase();
          const isSameName =
            item.name &&
            item.name.toLowerCase() === newGrade.name?.toLowerCase();
          setFilesBase64Grade('');
          return isSameColor || isSameName;
        });

        if (isDuplicate) {
          toast.error('Já existe uma variação com esta cor ou nome.');
          return;
        }

        if (!newGrade) {
          toast.error('Por favor, preencha a grade.');
          return;
        }

        if (newGrade.color === '') {
          if (newGrade.name === '') {
            toast.error('Por favor, preencha a grade com nome');
            return;
          }
        }

        if (newGrade && gradeSize.length > 0) {
          setGrade([...grade, newGrade]);
          onChangeFormInput(FormInputGrade.color)('');
          onChangeFormInput(FormInputGrade.nameGrade)('');
          onChangeFormInput(FormInputGrade.imageBase64Grade)('');
          setGradeSize([]);
          setPreviewGradesSizes([]);
          setPreviewFilesGrade('');
        }
      }}
      removeGrade={handleRemoveGrade}
      addSize={(e) => {
        e.preventDefault();

        const newGradeSize: GradeSize = {
          size: formData[FormInputGradeSize.size],
          stock: formData[FormInputGradeSize.stock],
          price: {
            price: Number(formData[FormInputGradeSize.price]),
            discount: parseInt(formData[FormInputGradeSize.discount]),
          },
        };
        if (newGradeSize && !inputTags.includes(newGradeSize.size.toString())) {
          setGradeSize([...gradeSize, newGradeSize]);
          onChangeFormInput(FormInputGradeSize.size)('');
          onChangeFormInput(FormInputGradeSize.stock)('');
          onChangeFormInput(FormInputGradeSize.price)('');
          onChangeFormInput(FormInputGradeSize.discount)('');
        }

        setPreviewGradesSizes([...previewGradesSizes, newGradeSize]);
      }}
      onChangeFormInput={onChangeFormInput}
      handleInputChange={handleInputChange}
      onSelectCategory={handleSelectedCaegories}
      onRemoveCategory={handleOnRemoveCategory}
      onAddTag={(tag) =>
        setInputTags((prev) => {
          if (prev === null) {
            return [tag];
          }
          return [...prev, tag];
        })
      }
      onRemoveTag={handleRemoveTag}
      onGrade={onGrade}
      onChangeFormFileInputGradeSize={(e) => {
        if (e.target.files && e.target.files.length > 0) {
          const file = e.target.files[0];
          setPreviewFilesGrade(URL.createObjectURL(file));
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            const base64 = reader.result?.toString();
            if (base64) {
              setFilesBase64Grade(base64);
            }
          };
        }
      }}
      onShippingCalc={handleShippingCalc}
      onSaveSection={handleSaveSection}
      onSaveCategory={handleSaveCategory}
      handleSaveImages={handleSaveImages}
      onClose={onClose}
      onSaveWarrantyDimensions={handleSaveWarrantyDimensions}
    />
  );
};
